/**
 * Router URL Sanitizer Plugin
 * 
 * This plugin overrides Vue Router navigation methods to ensure all URLs
 * are properly sanitized before navigation, preventing the common
 * [object Object] URL errors that can break navigation.
 * 
 * Created: May 2025 for improved reliability and security
 */

import { sanitizeUrl } from '~/utils/url-sanitizer';

export default defineNuxtPlugin((nuxtApp) => {
  if (!nuxtApp.$router) {
    console.warn('Router plugin: Vue Router not available');
    return;
  }

  // Store original navigation methods
  const originalPush = nuxtApp.$router.push;
  const originalReplace = nuxtApp.$router.replace;

  // Override router.push with sanitized version
  nuxtApp.$router.push = function(to: any, ...args: any[]) {
    // Sanitize the URL
    let sanitizedTo = to;
    
    if (typeof to === 'string') {
      sanitizedTo = sanitizeUrl(to);
    } else if (typeof to === 'object' && to !== null) {
      // If it's a raw location object, sanitize the path property if present
      if (to.path !== undefined) {
        to.path = sanitizeUrl(to.path);
      }
      
      // If it has a params object, ensure all params are strings
      if (to.params) {
        for (const key in to.params) {
          if (typeof to.params[key] === 'object' && to.params[key] !== null) {
            to.params[key] = String(to.params[key]);
          }
        }
      }
      
      sanitizedTo = to;
    }

    // Call original method with sanitized URL
    return originalPush.call(this, sanitizedTo, ...args);
  };

  // Override router.replace with sanitized version
  nuxtApp.$router.replace = function(to: any, ...args: any[]) {
    // Sanitize the URL
    let sanitizedTo = to;
    
    if (typeof to === 'string') {
      sanitizedTo = sanitizeUrl(to);
    } else if (typeof to === 'object' && to !== null) {
      // If it's a raw location object, sanitize the path property if present
      if (to.path !== undefined) {
        to.path = sanitizeUrl(to.path);
      }
      
      // If it has a params object, ensure all params are strings
      if (to.params) {
        for (const key in to.params) {
          if (typeof to.params[key] === 'object' && to.params[key] !== null) {
            to.params[key] = String(to.params[key]);
          }
        }
      }
      
      sanitizedTo = to;
    }

    // Call original method with sanitized URL
    return originalReplace.call(this, sanitizedTo, ...args);
  };

  // Also patch navigateTo helper globally if possible
  if (window && typeof window.navigateTo !== 'undefined') {
    const originalNavigateTo = window.navigateTo;
    
    window.navigateTo = function(to: any, options?: any) {
      let sanitizedTo = to;
      
      if (typeof to === 'string') {
        sanitizedTo = sanitizeUrl(to);
      } else if (typeof to === 'object' && to !== null) {
        // Handle Location object by sanitizing path
        if (to.path !== undefined) {
          to.path = sanitizeUrl(to.path);
        }
        
        // Handle any object params
        if (to.params) {
          for (const key in to.params) {
            if (typeof to.params[key] === 'object' && to.params[key] !== null) {
              to.params[key] = String(to.params[key]);
            }
          }
        }
        
        sanitizedTo = to;
      }
      
      return originalNavigateTo(sanitizedTo, options);
    };
  }

  // Log successful initialization
  console.log('Router sanitizer plugin initialized successfully');
});
